import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Empty,
  Popconfirm,
  Spin,
  Tooltip,
} from "antd";
import CustomModal from "../../components/generic/custommodal/CustomModal";
import PrimaryButton from "../../components/generic/buttons/PrimaryButton";
import SecondaryButton from "../../components/generic/buttons/SecondaryButton";
import SmartIcon from "../../components/generic/genericIcon/SmartIcon";
import SquareCardForm from "./SquareCardForm";
import "./savedCards.css";
import { useRecoilState } from "recoil";
import { useGetOrgIdentifier } from "../../experience/generic/hooks/useGetOrgIdentifier";
import apiService from "../../service/apiservice/apiService";
import { billingSavedCardsState } from "../../components/recoil/billingSavedCardsStore";
import { publishEvent } from "../../components/generic/EventPublisher";
import { useReadMemory } from "../../experience/generic/hooks/useReadMemory";
import MemoryType from "../../service/memory/enum/MemoryType";

function formatBrand(brand) {
  if (!brand) return "Card";
  return String(brand).replace(/_/g, " ");
}

function formatExpiry(month, year) {
  const m = month != null ? String(month).padStart(2, "0") : "—";
  const y = year != null ? String(year).slice(-2) : "—";
  return `${m}/${y}`;
}

const SavedCards = ({ officeId }) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const userJson = useReadMemory(MemoryType.USER)
  const cardFormRef = useRef(null);
  const [addCardModalOpen, setAddCardModalOpen] = useState(false);
  const [cardSubmitState, setCardSubmitState] = useState({
    isProcessing: false,
    canSubmit: false,
  });
  const [billingSavedCards, setBillingSavedCards] =
    useRecoilState(billingSavedCardsState);
  const cards = billingSavedCards.list;
  const loading = billingSavedCards.loading;
  const [defaultCardId, setDefaultCardId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [savedCardsRefreshKey, setSavedCardsRefreshKey] = useState(0);

  const closeAddCardModal = useCallback(() => {
    setAddCardModalOpen(false);
  }, []);

  const handleCardTokenized = useCallback(
    async (token) => {
      if (!token) return;
      const result = await apiService.post(
        "niramayafoundation/billing/api/v1/cards",
        {
          organizationIdentifier,
          sourceId: token,
          ...(officeId != null && { officeId }),
        },
        "Your payment method was saved successfully",
        "Could not save payment method",
      );

      if (result?.id) {
        setSavedCardsRefreshKey((k) => k + 1);
        closeAddCardModal();
        publishEvent("niramaya.billing.card.added", userJson, result)
      }
    },
    [closeAddCardModal, officeId, organizationIdentifier, userJson],
  );

  const handleSetDefault = useCallback(
    async (cardId) => {
      if (cardId === defaultCardId) return;

      setSettingDefaultId(cardId);
      const result = await apiService.put(
        "niramayafoundation/billing/api/v1/cards/markdefault",
        {
          organizationIdentifier,
          cardId,
          ...(officeId != null && { officeId }),
        },
        "Default payment method updated",
        "Could not update default payment method",
      );
      if (result !== undefined) {
        setDefaultCardId(cardId);
        setSavedCardsRefreshKey((k) => k + 1);
        publishEvent("niramaya.billing.card.default", userJson, result)
      }
      setSettingDefaultId(null);
    },
    [defaultCardId, officeId, organizationIdentifier, userJson],
  );

  const handleDelete = useCallback(
    async (card) => {
      if (card?.id == null) return;

      setDeletingId(card.id);
      try {
        const result = await apiService.delete(
          "niramayafoundation/billing/api/v1/cards",
          {
            organizationIdentifier,
            cardId: card.id,
            ...(officeId != null && { officeId }),
          },
          "Card removed",
          "Could not remove payment method",
        );
        if (result !== undefined) {
          setDefaultCardId((current) => (current === card.id ? null : current));
          setSavedCardsRefreshKey((k) => k + 1);
          publishEvent("niramaya.billing.card.delete", userJson, card)
        }
      } finally {
        setDeletingId(null);
      }
    },
    [officeId, organizationIdentifier, userJson],
  );

  useEffect(() => {
    const loadCards = async () => {
      setBillingSavedCards((prev) => ({ ...prev, loading: true }));
      try {
        const response = await apiService.get(
          "niramayafoundation/billing/api/v1/cards",
          {
            organizationIdentifier,
            ...(officeId != null && { officeId }),
          },
        );
        const list = Array.isArray(response) ? response : [];
        setBillingSavedCards({ list, loading: false });
        setDefaultCardId(list.find((c) => c.isDefault)?.id ?? null);
      } catch {
        setBillingSavedCards({ list: [], loading: false });
        setDefaultCardId(null);
      }
    };

    loadCards();
  }, [
    officeId,
    organizationIdentifier,
    savedCardsRefreshKey,
    setBillingSavedCards,
  ]);

  const sortedCards = useMemo(
    () =>
      [...cards].sort(
        (a, b) => Number(!!b.isDefault) - Number(!!a.isDefault),
      ),
    [cards],
  );

  return (
    <>
      <div className="saved-cards-section saved-cards-panel saved-cards-panel--sidebar">
        <header className="saved-cards-panel-header">
            <h2 className="saved-cards-panel-title">Payment Methods</h2>
          <PrimaryButton buttonText="Add Card" handleOnClick={() => setAddCardModalOpen(true)} />
        </header>

        <div className="saved-cards-scroll">
          {loading ? (
            <div className="saved-cards-list-loading">
              <Spin />
            </div>
          ) : cards.length === 0 ? (
            <Empty
              className="saved-cards-empty"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No saved cards yet. Add a payment method above."
            />
          ) : (
            <ul className="saved-cards-list">
              {sortedCards.map((record) => {
                const isDefault = Boolean(record.isDefault);
                const setting = settingDefaultId === record.id;
                const deleting = deletingId === record.id;
                const busy = setting || deleting;

                return (
                  <li
                    key={record.id}
                    className={[
                      "saved-card-row",
                      isDefault ? "saved-card-row--default" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <span className="saved-card-row-icon" aria-hidden>
                      <SmartIcon iconKey="creditCard" />
                    </span>
                    <div className="saved-card-row-body">
                      <div className="saved-card-row-text">
                        <div className="saved-card-row-top">
                          <span className="saved-card-row-brand">
                            {formatBrand(record.cardBrand)}
                          </span>
                          {isDefault ? (
                            <span className="saved-card-badge-default">
                              Default
                            </span>
                          ) : null}
                          {record.isExpired ? (
                            <span className="saved-card-badge-expired">
                              Expired
                            </span>
                          ) : null}
                        </div>
                        <div className="saved-card-row-meta">
                          <span className="saved-card-row-meta-dots">••••</span>{" "}
                          {record.cardLastFourDigits}
                          <span className="saved-card-row-meta-sep"> · </span>
                          <span className="saved-card-row-meta-exp-label">Exp</span>{" "}
                          {formatExpiry(record.expMonth, record.expYear)}
                        </div>
                      </div>
                    </div>
                  {!isDefault ? (
                    <div className="saved-card-row-actions">
                      <Tooltip title="Set as default" placement="bottom">
                        <span className="saved-cards-action-wrap">
                          {setting ? (
                            <Spin
                              size="small"
                              className="saved-cards-action-spin"
                            />
                          ) : (
                            <span
                              className={
                                busy ? "saved-cards-smarticon--muted" : undefined
                              }
                            >
                              <SmartIcon
                                iconKey="default"
                                handleIconClick={
                                  busy
                                    ? undefined
                                    : () => handleSetDefault(record.id)
                                }
                              />
                            </span>
                          )}
                        </span>
                      </Tooltip>
                      <Tooltip title="Delete" placement="bottom">
                        <span>
                          <Popconfirm
                            title="Are you sure you want to remove this card?"
                            disabled={busy}
                            placement="top"
                            okText="Confirm"
                            cancelText="Cancel"
                            onConfirm={() => handleDelete(record)}
                            overlayClassName="article-delete-popconfirm"
                          >
                            <span className="saved-cards-action-wrap">
                              {deleting ? (
                                <Spin
                                  size="small"
                                  className="saved-cards-action-spin"
                                />
                              ) : (
                                <span
                                  className={
                                    busy
                                      ? "saved-cards-smarticon--muted"
                                      : undefined
                                  }
                                >
                                  <SmartIcon iconKey="delete" />
                                </span>
                              )}
                            </span>
                          </Popconfirm>
                        </span>
                      </Tooltip>
                    </div>
                  ) : null}
                </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {addCardModalOpen && (
        <CustomModal
          isModalOpen={addCardModalOpen}
          closeModal={closeAddCardModal}
          footer={
            <>
              <SecondaryButton
                buttonText="Close"
                handleOnClick={closeAddCardModal}
              />
              <PrimaryButton
                buttonText="Add"
                handleOnClick={() => cardFormRef.current?.submitCard()}
                buttonDisable={
                  !cardSubmitState.canSubmit || cardSubmitState.isProcessing
                }
                loading={cardSubmitState.isProcessing}
              />
            </>
          }
          title="Add Payment Method"
          closable
        >
          <SquareCardForm
            ref={cardFormRef}
            onCardTokenized={handleCardTokenized}
            onSubmitStateChange={setCardSubmitState}
          />
        </CustomModal>
      )}
    </>
  );
};

export default SavedCards;
